<template>
  <div>
    <PageHeader :gradientType="gradientType">
      <router-link
        to="/admin/member-profiles"
        tag="button"
        class="btn btn-link text-uppercase"
        style="color: #484c54"
      >
        &#8592; Back to member profiles
      </router-link>
    </PageHeader>
    <PageContent>
      <template #filterBar>
        <form>
          <div class="row">
            <div class="col">
              <base-input class="mb-0" label="First Name">
                <a-input disabled v-model="memberData.first_name" />
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input class="mb-0" label="Last Name">
                <a-input disabled v-model="memberData.last_name" />
              </base-input>
            </div>
          </div>
          <hr />
          <div class="row">
            <div class="col">
              <base-input label="Total Points">
                <a-input
                  disabled
                  :value="formattedValue(totalPoints, 'Total Points')"
                />
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input label="Total Active Points">
                <a-input
                  disabled
                  :value="
                    formattedValue(
                      memberData.current_points_balance_active,
                      'Total Active Points'
                    )
                  "
                />
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input label="Total Expired Points">
                <a-input
                  disabled
                  :value="
                    formattedValue(
                      memberData.total_points_expired,
                      'Total Expired Points'
                    )
                  "
                />
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input label="Total Purchase">
                <a-input
                  disabled
                  :value="formattedValue(total_purchased, 'Total Purchase')"
                />
              </base-input>
            </div>
          </div>
        </form>
      </template>

      <template #pageTable>
        <div class="row">
          <div class="col">
            <base-input label="Transaction Type">
              <a-select
                v-model="table.filters.transaction_type_id"
                style="width: 100%"
                allow-clear
              >
                <a-select-option
                  v-for="transactionType in transactionTypes"
                  :key="transactionType.id"
                  :value="transactionType.id"
                >
                  {{ transactionType.name }}
                </a-select-option>
              </a-select>
            </base-input>
          </div>
          <div class="col">
            <base-input label="Site">
              <a-select
                v-model="table.filters.site_id"
                style="width: 100%"
                allow-clear
              >
                <a-select-option
                  v-for="site in sites"
                  :key="site.id"
                  :value="site.id"
                >
                  {{ site.name }}
                </a-select-option>
              </a-select>
            </base-input>
          </div>
          <div class="col">
            <base-input label="Transaction Date">
              <a-range-picker
                :default-value="null"
                :format="dateFormat"
                @change="dateRangeOnChange"
              />
            </base-input>
          </div>
          <div class="col">
            <base-input label="Transaction ID">
              <a-input v-model="table.filters.transaction_id" />
            </base-input>
          </div>
          <div class="col d-flex align-items-center">
            <b-button
              size="sm"
              class="btn-block"
              variant="info"
              @click="applyFilter"
            >
              Apply Filter
            </b-button>
          </div>
        </div>
        <PageTable
          :tableTitle="table.title"
          :apiUrl="table.apiUrl"
          :tableFields="table.fields"
          :appendedParams="table.filters"
          :sortOrder="table.sortOrder"
        >
          <template #headerButtons>
            <base-dropdown class="nav-link pr-0 float-right" position="right">
              <div class="media align-items-center" slot="title">
                <i class="fa fa-ellipsis-v float-right ml-2 btn-sm"></i>
              </div>
              <template>
                <div class="dropdown-header noti-title">
                  <h6 class="text-overflow m-0">ACTIONS</h6>
                </div>

                <div class="dropdown-item">
                  <downloadexcel
                    :fetch="fetchJsonData"
                    :escapeCsv="false"
                    :fields="excelExportOpts.jsonFields"
                    :type="excelExportOpts.filetype"
                    :name="excelExportOpts.filename"
                    :before-generate="beforeGenerateData"
                  >
                    <i class="fa fa-download"></i> <span>Export to excel</span>
                  </downloadexcel>
                </div>
              </template>
            </base-dropdown>
          </template>
        </PageTable>
      </template>
    </PageContent>
  </div>
</template>

<script>
import PageHeader from '../../components/Page/Header.vue';
import PageContent from '../../components/Page/Content.vue';
import PageTable from '../../components/Page/Table.vue';
import HTTPRequest from '../../controllers/httpRequest';
import moment from 'moment-timezone';
import downloadexcel from 'vue-json-excel';

export default {
  name: 'MemberTransaction',
  components: {
    PageHeader,
    PageContent,
    PageTable,
    downloadexcel
  },
  data() {
    return {
      gradientType: 'gradient-blue',
      member: null,
      total_purchased: 0,
      nearest_points_expired: 0,
      memberData: {
        first_name: null,
        last_name: null,
        membership_program: null,
        membership_level: null,
        total_points_earned: 0,
        total_points_redeemed: 0,
        total_points_expired: 0
      },
      dateFormat: 'YYYY-MM-DD',
      table: {
        title: 'Member Transaction Histories',
        apiUrl: '/api/v1/clp_transactions/member_transactions',
        fields: [
          { name: 'site_name', title: 'Site' },
          { name: 'terminal_code', title: 'Terminal Code' },
          { name: 'reference_transaction_id', title: 'Transaction ID' },
          {
            name: 'created_at',
            title: 'Processed Date', //Processed Date: when CLP processed the data
            formatter(date) {
              if (moment(date).isValid()) {
                return moment(date).format('MM/DD/YYYY');
              }
              return null;
            }
          },
          {
            name: 'reference_transaction_date',
            title: 'Transaction Date', //Transaction Date: POS transaction's date
            formatter(date) {
              if (moment(date).isValid()) {
                return moment(date).format('MM/DD/YYYY');
              }
              return null;
            }
          },
          {
            name: 'status',
            title: 'Status',
            formatter(value) {
              if (value !== '') {
                switch (value) {
                  case 'V':
                    return 'Voided';
                  default:
                    return 'Posted';
                }
              }
              return null;
            }
          },
          { name: 'transactionType.name', title: 'Transaction Type' },
          {
            name: 'purchase_amount',
            title: 'Purchase Amount',
            formatter(points) {
              return points
                ? Number(points).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })
                : 0.0;
            }
          },
          {
            name: 'points',
            title: 'Points',
            formatter(points) {
              return points
                ? Number(points).toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })
                : 0;
            }
          },
          { name: 'creator.name', title: 'Created By' },
          { name: 'remarks', title: 'Remarks' }
        ],
        sortOrder: [
          {
            field: 'created_at',
            direction: 'desc'
          }
        ],
        filters: {
          status: [],
          member_id: null,
          transaction_type_id: null,
          site_id: null,
          transaction_id: null,
          transaction_date_from: null,
          transaction_date_to: null,
          export_data: 0
        }
      },
      transactionTypes: [],
      sites: [],
      excelExportOpts: {
        jsonFields: {
          Site: 'site_name',
          Terminal: 'terminal_code',
          'Transaction ID': 'reference_transaction_id',
          'Processed Date': {
            field: 'created_at',
            callback: (date) => {
              if (moment(date).isValid()) {
                return moment(date).format('MM/DD/YYYY');
              }
              return null;
            }
          },
          'Transaction Date': {
            field: 'reference_transaction_date',
            callback: (date) => {
              if (moment(date).isValid()) {
                return moment(date).format('MM/DD/YYYY');
              }
              return null;
            }
          },
          Status: {
            field: 'status',
            callback: (value) => {
              if (value !== '') {
                switch (value) {
                  case 'V':
                    return 'Voided';
                  default:
                    return 'Posted';
                }
              }
              return null;
            }
          },
          'Transaction Type': 'transactionType.name',
          'Purchase Amount': {
            field: 'purchase_amount',
            callback: (amount) => {
              return amount ? amount.toFixed(2) : 0.0;
            }
          },
          Points: {
            field: 'points',
            callback: (points) => {
              return points ? points.toFixed(2) : 0.0;
            }
          },
          'Created By': 'creator.name',
          Remarks: 'remarks'
        },
        filetype: 'csv',
        filename: 'member-transaction-list.csv'
      }
    };
  },
  methods: {
    moment,
    formattedValue(value, label) {
      // If value is null, undefined, or empty string, show 0.00
      if (value === null || value === undefined || value === '') {
        return '0.00';
      }

      return label === 'Total Purchase'
        ? Number(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        : Number(value).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
    },
    applyFilter() {
      this.$root.$emit('refreshTable');
    },

    async getMemberData(memberId) {
      const data = await HTTPRequest.getRequest(
        '/api/v1/clp_members/' + memberId
      );

      this.memberData = data;
    },
    async getMemberTotalPurchase(memberId) {
      const data = await HTTPRequest.getRequest(
        '/api/v1/clp_transactions/' + memberId + '/total_purchased'
      );
      this.total_purchased = data?.purchase_amount ?? 0;
    },
    async getTransactionTypes() {
      const response = await HTTPRequest.getRequest(
        '/api/v1/clp_transaction_types'
      );
      this.transactionTypes = response.data;
    },

    async getSites() {
      const response = await HTTPRequest.getRequest('/api/v1/sites', {
        per_page: 1000
      });
      this.sites = response.data;
    },

    dateRangeOnChange(dateObj, dateString) {
      this.table.filters.transaction_date_from = dateString[0];
      this.table.filters.transaction_date_to = dateString[1];
      console.log(dateObj);
    },

    async fetchJsonData() {
      this.table.filters.export_data = 1;
      const _params = this.table.filters;
      const _apiUrl = this.table.apiUrl;

      let arr = [];
      for (let key in _params) {
        if (_params[key] != null) {
          arr.push(key + '=' + _params[key]);
        }
      }
      const queryString = arr.join('&');
      const data = await HTTPRequest.getRequest(_apiUrl + '?' + queryString);
      return data;
    },

    beforeGenerateData() {
      this.$message.loading('Generating data', 1);
    },

    async getMemberTotalExpiringPoints(memberId) {
      const response = await HTTPRequest.getRequest(
        '/api/v1/clp_transactions/' + memberId + '/total_points_expired'
      );

      if (response.data) {
        this.nearest_points_expired = response.data.points;
      }
    }
  },

  computed: {
    totalPoints: function () {
      return (
        parseFloat(this.memberData.current_points_balance_active) +
        parseFloat(this.memberData.current_points_balance_expiring)
      );
    }
  },

  created() {
    this.member = this.$store.getters.getCurrentMember;
    if (this.member != null) {
      this.table.filters.member_id = this.member.id;
    } else {
      this.$router.go(-1);
    }

    this.getMemberTotalPurchase(this.member.id);
    this.getMemberData(this.member.id);
    this.getTransactionTypes();
    this.getSites();
    this.getMemberTotalExpiringPoints(this.member.id);
    console.log('Timezone: ', moment.tz.guess());
  },

  watch: {
    'table.filters.transaction_date_from': function (val) {
      if (val == '') {
        this.table.filters.transaction_date_from = null;
      }
    },

    'table.filters.transaction_date_to': function (val) {
      if (val == '') {
        this.table.filters.transaction_date_to = null;
      }
    }
  }
};
</script>

<style scoped></style>
